import { useLanguage } from "@/hooks/useLanguage";
import { Link, useLocation } from "react-router-dom";
import { useState, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import { Menu, ChevronDown, ChevronRight } from "lucide-react";
import LanguageDropdown from "@/components/LanguageDropdown";
import ThemeToggle from "@/components/ThemeToggle";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import type { Language } from "@/lib/i18n";

const MobileMenu = lazy(() => import("@/components/MobileMenu"));

type CategoryItem = { slug: string } & Record<Language, string>;

const lampCategories: CategoryItem[] = [
  { slug: "pendelleuchten", de: "Pendelleuchten", en: "Pendant Lights", it: "Sospensioni", es: "Lámparas colgantes", nl: "Hanglampen" },
  { slug: "deckenleuchten", de: "Deckenleuchten", en: "Ceiling Lights", it: "Plafoniere", es: "Plafones", nl: "Plafondlampen" },
  { slug: "wandleuchten", de: "Wandleuchten", en: "Wall Lights", it: "Applique", es: "Apliques", nl: "Wandlampen" },
  { slug: "stehleuchten", de: "Stehleuchten", en: "Floor Lamps", it: "Lampade da terra", es: "Lámparas de pie", nl: "Vloerlampen" },
  { slug: "tischleuchten", de: "Tischleuchten", en: "Table Lamps", it: "Lampade da tavolo", es: "Lámparas de mesa", nl: "Tafellampen" },
  { slug: "outdoor", de: "Außenleuchten", en: "Outdoor Lights", it: "Illuminazione esterna", es: "Iluminación exterior", nl: "Buitenverlichting" },
  { slug: "architektonisch", de: "Architekturleuchten", en: "Architectural Lights", it: "Illuminazione architettonica", es: "Iluminación arquitectónica", nl: "Architecturale verlichting" },
];

const furnitureCategories: CategoryItem[] = [
  { slug: "sofas", de: "Sofas", en: "Sofas", it: "Divani", es: "Sofás", nl: "Bank" },
  { slug: "sessel", de: "Sessel", en: "Armchairs", it: "Poltrone", es: "Sillones", nl: "Fauteuils" },
  { slug: "betten", de: "Betten", en: "Beds", it: "Letti", es: "Camas", nl: "Bedden" },
  { slug: "tische-stuehle", de: "Tische & Stühle", en: "Tables & Chairs", it: "Tavoli e sedie", es: "Mesas y sillas", nl: "Tafels & stoelen" },
  { slug: "accessoires", de: "Accessoires", en: "Accessories", it: "Accessori", es: "Accesorios", nl: "Accessoires" },
];

const TRANSITION = "background 600ms cubic-bezier(0.22, 1, 0.36, 1), border-color 600ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 600ms cubic-bezier(0.22, 1, 0.36, 1), backdrop-filter 600ms cubic-bezier(0.22, 1, 0.36, 1), -webkit-backdrop-filter 600ms cubic-bezier(0.22, 1, 0.36, 1), max-width 700ms cubic-bezier(0.22, 1, 0.36, 1), margin-top 700ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 700ms cubic-bezier(0.22, 1, 0.36, 1), height 500ms cubic-bezier(0.22, 1, 0.36, 1), padding 500ms cubic-bezier(0.22, 1, 0.36, 1), font-size 500ms cubic-bezier(0.22, 1, 0.36, 1), color 300ms ease";

const SCROLL_ENTER_THRESHOLD = 22;
const SCROLL_EXIT_THRESHOLD = 8;

const Navbar = () => {
  const { language, t, p } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false,
  );

  const isHeroPage =
    location.pathname === `/${language}` ||
    location.pathname === `/${language}/` ||
    location.pathname === "/" ||
    location.pathname === p("about");

  const megaTimeout = useRef<ReturnType<typeof setTimeout>>();
  const rafRef = useRef(0);
  const scrolledRef = useRef(false);
  const navShellRef = useRef<HTMLDivElement>(null);

  const syncThemeMode = useCallback(() => {
    setIsDarkMode(document.documentElement.classList.contains("dark"));
  }, []);

  // ── Theme tracking ──
  useEffect(() => {
    syncThemeMode();
    const observer = new MutationObserver(syncThemeMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [syncThemeMode]);

  // ── Scroll detection with hysteresis ──
  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      const y = window.scrollY;
      const nextScrolled = scrolledRef.current
        ? y > SCROLL_EXIT_THRESHOLD
        : y > SCROLL_ENTER_THRESHOLD;

      if (nextScrolled !== scrolledRef.current) {
        scrolledRef.current = nextScrolled;
        setScrolled(nextScrolled);
      }

      rafRef.current = 0;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [handleScroll]);

  // Close mega on scroll (same pattern as language switcher)
  useEffect(() => {
    if (!megaOpen) return;
    const handleScrollClose = () => setMegaOpen(false);
    window.addEventListener("scroll", handleScrollClose, { passive: true });
    window.visualViewport?.addEventListener("scroll", handleScrollClose);
    return () => {
      window.removeEventListener("scroll", handleScrollClose);
      window.visualViewport?.removeEventListener("scroll", handleScrollClose);
    };
  }, [megaOpen]);

  // ── Mega menu hover logic ──
  const handleMegaEnter = () => {
    clearTimeout(megaTimeout.current);
    setLanguageMenuOpen(false);
    setMegaOpen(true);
  };

  const handleMegaLeave = () => {
    megaTimeout.current = setTimeout(() => setMegaOpen(false), 200);
  };

  // Close menus on route change
  useEffect(() => {
    setMegaOpen(false);
    setLanguageMenuOpen(false);
    setMobileOpen(false);
    scrolledRef.current = window.scrollY > SCROLL_ENTER_THRESHOLD;
    setScrolled(scrolledRef.current);
  }, [location.pathname]);

  useEffect(() => {
    return () => clearTimeout(megaTimeout.current);
  }, []);

  const navLinks = [
    { to: p("about"), label: t.nav.about },
    { to: p("manufacturers"), label: t.nav.manufacturers },
    { to: p("contact"), label: t.nav.contact },
  ];

  // ── Dynamic colors ──
  const showCapsule = scrolled;
  const navSurfaceOpen = megaOpen || languageMenuOpen;
  const isTransparent = !showCapsule && !navSurfaceOpen && isHeroPage;

  const transparentMutedText = isDarkMode
    ? "hsl(var(--foreground) / 0.80)"
    : "hsl(var(--foreground) / 0.88)";
  const transparentStrongText = isDarkMode
    ? "hsl(var(--foreground) / 0.95)"
    : "hsl(var(--foreground) / 0.98)";

  // Hero nav text: white in light mode so it reads over the dark hero image
  const heroNavText = isDarkMode ? transparentStrongText : "rgba(255,255,255,0.95)";
  const heroNavMuted = isDarkMode ? transparentMutedText : "rgba(255,255,255,0.82)";

  const linkColor = isTransparent ? heroNavMuted : "hsl(var(--foreground) / 0.88)";
  const activeLinkColor = isTransparent ? heroNavText : "hsl(var(--foreground) / 0.98)";
  const borderColor = isTransparent ? "hsl(var(--border) / 0.78)" : "hsl(var(--border) / 0.98)";
  const heroControlColor = isTransparent && isHeroPage && !isDarkMode ? heroNavText : undefined;
  const controlTextColor = heroControlColor ?? (isTransparent ? transparentStrongText : "hsl(var(--foreground) / 0.92)");
  const dropdownItemColor = "hsl(var(--foreground) / 0.84)";

  // Product prefix paths
  const productsPath = p("products");
  const lampPrefix = p("productsPrefix", "lampen");
  const furniturePrefix = p("productsPrefix", "moebel");

  return (
    <>
      {/* ── Mobile/Tablet: Liquid glass backdrop ── */}
      <div
        className="pointer-events-none fixed left-0 right-0 top-0 z-[48] lg:hidden"
        aria-hidden="true"
        style={{
          transition: "opacity 800ms cubic-bezier(0.22, 1, 0.36, 1)",
          opacity: scrolled ? 1 : 0.34,
          height: 80,
          backdropFilter: "blur(34px) saturate(1.12) brightness(1.02)",
          WebkitBackdropFilter: "blur(34px) saturate(1.12) brightness(1.02)",
          maskImage: "linear-gradient(to bottom, black 34%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 34%, transparent 100%)",
        }}
      />

      {/* ── Mobile/Tablet: Subtle edge vignette ── */}
      <div
        className="pointer-events-none fixed left-0 right-0 top-0 z-[47] lg:hidden"
        aria-hidden="true"
        style={{
          transition: "opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
          opacity: scrolled ? 0.55 : 0,
          height: 72,
          background:
            "linear-gradient(to bottom, hsl(var(--background) / 0.14) 0%, transparent 100%)",
          maskImage: "linear-gradient(to bottom, black 22%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 22%, transparent 100%)",
        }}
      />

      <nav
        className="fixed left-0 right-0 top-0 z-50 px-5 sm:px-6 md:px-8 lg:px-0"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div
          ref={navShellRef}
          className={`mx-auto ${showCapsule ? "navbar-liquid-capsule" : ""}`}
          style={{
            transition: TRANSITION,
            overflow: "visible",
            maxWidth: showCapsule
              ? "min(960px, calc(100vw - clamp(40px, 7vw, 72px)))"
              : "100%",
            marginTop: showCapsule ? 10 : 0,
            borderRadius: showCapsule ? 9999 : 0,
            ...(!showCapsule
              ? {
                  background: "transparent",
                  border: "0.5px solid transparent",
                  backdropFilter: "blur(0px) saturate(1)",
                  WebkitBackdropFilter: "blur(0px) saturate(1)",
                  boxShadow: "none",
                }
              : {}),
          }}
        >
          <div
            className="flex items-center justify-between px-5 lg:px-6"
            style={{
              transition: TRANSITION,
              height: scrolled ? 52 : 60,
            }}
          >
            {/* ── Logo ── */}
            <Link
              to={p("home")}
              className="font-display font-semibold uppercase tracking-[0.18em]"
              style={{
                transition: TRANSITION,
                fontSize: scrolled ? "0.75rem" : "0.8125rem",
                color: isTransparent ? heroNavText : "hsl(var(--foreground))",
              }}
            >
              Frank Pistorius
            </Link>

            {/* ── Desktop center nav ── */}
            <div className="hidden items-center gap-7 lg:flex">
              {/* Portfolio dropdown — uses Radix Popover, same engine as language switcher */}
              <Popover open={megaOpen} onOpenChange={setMegaOpen}>
                <div
                  className="relative"
                  onMouseEnter={handleMegaEnter}
                  onMouseLeave={handleMegaLeave}
                >
                  <PopoverTrigger asChild>
                    <button
                      className="flex items-center gap-1 text-[13px] font-medium tracking-wide bg-transparent border-none cursor-pointer"
                      style={{
                        transition: "color 200ms",
                        color: location.pathname.startsWith(productsPath)
                          ? activeLinkColor
                          : linkColor,
                      }}
                      onClick={(e) => {
                        // Navigate to products on click instead of toggling
                        e.preventDefault();
                        setMegaOpen(false);
                        window.location.href = productsPath;
                      }}
                      onMouseEnter={handleMegaEnter}
                    >
                      {t.nav.products}
                      <ChevronDown
                        className="h-3 w-3"
                        style={{
                          transition: "transform 200ms cubic-bezier(0.22,1,0.36,1)",
                          transform: megaOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      />
                    </button>
                  </PopoverTrigger>

                  <PopoverContent
                    side="bottom"
                    align="center"
                    sideOffset={12}
                    collisionPadding={{ top: 12, right: 16, bottom: 12, left: 16 }}
                    className="liquid-panel z-[260] w-[min(560px,calc(100vw-4rem))] rounded-[1.375rem] p-6 border-none"
                    onMouseEnter={handleMegaEnter}
                    onMouseLeave={handleMegaLeave}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                  >
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <Link
                          to={`${productsPath}?tab=lamps`}
                          onClick={() => setMegaOpen(false)}
                          className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest"
                          style={{ color: "hsl(var(--foreground) / 0.98)" }}
                        >
                          {t.nav.lamps}
                          <ChevronRight className="h-3 w-3" />
                        </Link>
                        <div className="space-y-0.5">
                          {lampCategories.map((cat) => (
                            <Link
                              key={cat.slug}
                              to={`${lampPrefix}/${cat.slug}`}
                              onClick={() => setMegaOpen(false)}
                              className="block rounded-lg px-2.5 py-1.5 text-sm transition-colors hover:bg-accent/70"
                              style={{ color: dropdownItemColor }}
                            >
                              {cat[language]}
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Link
                          to={`${productsPath}?tab=furniture`}
                          onClick={() => setMegaOpen(false)}
                          className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest"
                          style={{ color: "hsl(var(--foreground) / 0.98)" }}
                        >
                          {t.nav.furniture}
                          <ChevronRight className="h-3 w-3" />
                        </Link>
                        <div className="space-y-0.5">
                          {furnitureCategories.map((cat) => (
                            <Link
                              key={cat.slug}
                              to={`${furniturePrefix}/${cat.slug}`}
                              onClick={() => setMegaOpen(false)}
                              className="block rounded-lg px-2.5 py-1.5 text-sm transition-colors hover:bg-accent/70"
                              style={{ color: dropdownItemColor }}
                            >
                              {cat[language]}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div
                      className="mt-5 border-t pt-4"
                      style={{ borderColor: "hsl(var(--border) / 0.52)" }}
                    >
                      <Link
                        to={p("manufacturers")}
                        onClick={() => setMegaOpen(false)}
                        className="flex items-center gap-2 text-xs font-medium transition-colors"
                        style={{ color: "hsl(var(--foreground) / 0.82)" }}
                      >
                        {t.nav.allManufacturers}
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </PopoverContent>
                </div>
              </Popover>

              {navLinks.map((link, i) => (
                <Link
                  key={i}
                  to={link.to}
                  className="text-[13px] font-medium tracking-wide"
                  style={{
                    transition: "color 200ms",
                    color: location.pathname === link.to ? activeLinkColor : linkColor,
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* ── Desktop right side ── */}
            <div className="hidden items-center gap-3 lg:flex">
              {/* CTA */}
              <Link
                to={productsPath}
                className="whitespace-nowrap rounded-full px-5 py-2 text-[12px] font-semibold tracking-[0.04em] transition-all duration-300"
                style={
                  isTransparent
                    ? {
                        background: "hsl(var(--background) / 0.62)",
                        color: "hsl(var(--foreground) / 0.95)",
                        border: `1px solid ${borderColor}`,
                        backdropFilter: "blur(14px) saturate(1.05)",
                        WebkitBackdropFilter: "blur(14px) saturate(1.05)",
                      }
                    : {
                        background: "hsl(var(--foreground))",
                        color: "hsl(var(--background))",
                        border: "1px solid transparent",
                      }
                }
              >
                {t.nav.browseLighting}
              </Link>

              {/* Language dropdown */}
              <LanguageDropdown
                isTransparent={isTransparent}
                borderColor={borderColor}
                textColor={controlTextColor}
                onOpenChange={(open) => {
                  setLanguageMenuOpen(open);
                  if (open) setMegaOpen(false);
                }}
              />

              {/* Theme toggle */}
              <ThemeToggle isTransparent={isTransparent} borderColor={borderColor} textColor={controlTextColor} />
            </div>

            {/* ── Mobile: lang + theme + hamburger ── */}
            <div className="flex items-center gap-2 lg:hidden">
              <LanguageDropdown
                isTransparent={isTransparent}
                borderColor={borderColor}
                textColor={controlTextColor}
                onOpenChange={setLanguageMenuOpen}
              />
              <ThemeToggle isTransparent={isTransparent} borderColor={borderColor} textColor={controlTextColor} />

              {/* Hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className="flex h-10 w-10 items-center justify-center"
                style={{
                  color: heroControlColor ?? (isTransparent ? transparentStrongText : "hsl(var(--foreground))"),
                }}
                aria-label="Open menu"
                aria-expanded={mobileOpen}
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Fullscreen mobile menu ── */}
      <Suspense fallback={null}>
        <MobileMenu
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          language={language}
          lampCategories={lampCategories}
          furnitureCategories={furnitureCategories}
          navLinks={navLinks}
          browseLightingLabel={t.nav.browseLighting}
        />
      </Suspense>
    </>
  );
};

export default Navbar;
